/**
 * Dashboard Metrics Cards Component
 * Displays key metrics with trend indicators and sparkline-style visuals
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  FileText,
  DollarSign,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: string | number;
  trend?: number; // percentage change
  trendLabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  sparklineData?: number[];
}

function MetricCard({
  title,
  value,
  trend,
  trendLabel,
  icon: Icon,
  loading,
  sparklineData,
}: MetricCardProps) {
  const getTrendColor = (t: number | undefined) => {
    if (t === undefined) return "text-muted-foreground";
    if (t > 0) return "text-success";
    if (t < 0) return "text-destructive";
    return "text-muted-foreground";
  };

  const getTrendIcon = (t: number | undefined) => {
    if (t === undefined) return null;
    if (t > 0) return <TrendingUp className="h-4 w-4" />;
    if (t < 0) return <TrendingDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  // Mini sparkline component
  const Sparkline = ({ data }: { data: number[] }) => {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 60;
    const height = 20;
    const points = data
      .map((val, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((val - min) / range) * height;
        return `${x},${y}`;
      })
      .join(" ");

    const isPositive = data[data.length - 1] >= data[0];

    return (
      <svg width={width} height={height} className="ml-2">
        <polyline
          points={points}
          fill="none"
          stroke={isPositive ? "var(--success)" : "var(--destructive)"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <Skeleton className="h-4 w-24 animate-shimmer" />
          <Skeleton className="h-4 w-4 animate-shimmer" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 animate-shimmer mb-2" />
          <Skeleton className="h-4 w-20 animate-shimmer" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-2xl font-bold">{value}</div>
            {trend !== undefined && (
              <div
                className={cn(
                  "flex items-center gap-1 text-sm",
                  getTrendColor(trend)
                )}
              >
                {getTrendIcon(trend)}
                <span>
                  {trend > 0 ? "+" : ""}
                  {trend.toFixed(1)}%
                </span>
                {trendLabel && (
                  <span className="text-muted-foreground ml-1">
                    {trendLabel}
                  </span>
                )}
              </div>
            )}
          </div>
          {sparklineData && <Sparkline data={sparklineData} />}
        </div>
      </CardContent>
    </Card>
  );
}

// Transaction type inferred from API
type Transaction = {
  id: number;
  date: number;
  amount: number;
  type: string;
  category: string | null;
};

export function MetricsCards() {
  const { selectedCompanyId } = useCompany();

  // Fetch transaction data for metrics (API returns array directly)
  const { data: transactions = [], isLoading } = trpc.transaction.list.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  // Calculate metrics from transaction data
  const calculateMetrics = () => {
    if (!transactions || transactions.length === 0) {
      return {
        totalTransactions: 0,
        monthlyRevenue: 0,
        monthlyExpenses: 0,
        categorizedRate: 0,
        revenueTrend: 0,
        expenseTrend: 0,
        transactionTrend: 0,
        revenueSparkline: [] as number[],
        expenseSparkline: [] as number[],
      };
    }

    const txList = transactions as Transaction[];
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
    const sixtyDaysAgo = now - 60 * 24 * 60 * 60 * 1000;

    // Current month transactions
    const currentMonth = txList.filter(
      (tx: Transaction) => tx.date >= thirtyDaysAgo
    );
    const previousMonth = txList.filter(
      (tx: Transaction) => tx.date >= sixtyDaysAgo && tx.date < thirtyDaysAgo
    );

    // Revenue (credits)
    const currentRevenue = currentMonth
      .filter((tx: Transaction) => tx.type === "credit")
      .reduce((sum: number, tx: Transaction) => sum + tx.amount, 0);
    const previousRevenue = previousMonth
      .filter((tx: Transaction) => tx.type === "credit")
      .reduce((sum: number, tx: Transaction) => sum + tx.amount, 0);

    // Expenses (debits)
    const currentExpenses = currentMonth
      .filter((tx: Transaction) => tx.type === "debit")
      .reduce((sum: number, tx: Transaction) => sum + Math.abs(tx.amount), 0);
    const previousExpenses = previousMonth
      .filter((tx: Transaction) => tx.type === "debit")
      .reduce((sum: number, tx: Transaction) => sum + Math.abs(tx.amount), 0);

    // Trends
    const revenueTrend = previousRevenue
      ? ((currentRevenue - previousRevenue) / previousRevenue) * 100
      : 0;
    const expenseTrend = previousExpenses
      ? ((currentExpenses - previousExpenses) / previousExpenses) * 100
      : 0;
    const transactionTrend = previousMonth.length
      ? ((currentMonth.length - previousMonth.length) / previousMonth.length) *
        100
      : 0;

    // Categorization rate
    const categorized = txList.filter(
      (tx: Transaction) => tx.category && tx.category !== "Uncategorized"
    );
    const categorizedRate = txList.length
      ? (categorized.length / txList.length) * 100
      : 0;

    // Generate sparkline data (last 7 days)
    const revenueSparkline: number[] = [];
    const expenseSparkline: number[] = [];
    for (let i = 6; i >= 0; i--) {
      const dayStart = now - (i + 1) * 24 * 60 * 60 * 1000;
      const dayEnd = now - i * 24 * 60 * 60 * 1000;
      const dayTx = txList.filter(
        (tx: Transaction) => tx.date >= dayStart && tx.date < dayEnd
      );
      revenueSparkline.push(
        dayTx
          .filter((tx: Transaction) => tx.type === "credit")
          .reduce((sum: number, tx: Transaction) => sum + tx.amount, 0)
      );
      expenseSparkline.push(
        dayTx
          .filter((tx: Transaction) => tx.type === "debit")
          .reduce(
            (sum: number, tx: Transaction) => sum + Math.abs(tx.amount),
            0
          )
      );
    }

    return {
      totalTransactions: txList.length,
      monthlyRevenue: currentRevenue,
      monthlyExpenses: currentExpenses,
      categorizedRate,
      revenueTrend,
      expenseTrend,
      transactionTrend,
      revenueSparkline,
      expenseSparkline,
    };
  };

  const metrics = calculateMetrics();

  const formatChf = (amount: number) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  if (!selectedCompanyId) {
    return null;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="Total Transactions"
        value={metrics.totalTransactions.toLocaleString()}
        trend={metrics.transactionTrend}
        trendLabel="vs last month"
        icon={FileText}
        loading={isLoading}
      />
      <MetricCard
        title="Monthly Revenue"
        value={formatChf(metrics.monthlyRevenue)}
        trend={metrics.revenueTrend}
        trendLabel="vs last month"
        icon={DollarSign}
        loading={isLoading}
        sparklineData={metrics.revenueSparkline}
      />
      <MetricCard
        title="Monthly Expenses"
        value={formatChf(metrics.monthlyExpenses)}
        trend={metrics.expenseTrend}
        trendLabel="vs last month"
        icon={DollarSign}
        loading={isLoading}
        sparklineData={metrics.expenseSparkline}
      />
      <MetricCard
        title="Categorized"
        value={`${metrics.categorizedRate.toFixed(0)}%`}
        icon={CheckCircle2}
        loading={isLoading}
      />
    </div>
  );
}
